<script setup lang="ts">
/**
 * 事件属性行布局组件。
 * 左侧标签 + 右侧控件插槽，用于统一属性面板的两列排版。
 */
defineProps<{
  /** 第一列显示的属性名 */
  label: string
  /** 第二列内容偏上对齐（多行输入等） */
  alignStart?: boolean
  /** 第二列内纵向堆叠（如多行文本占满宽） */
  valueStack?: boolean
}>()
</script>

<template>
  <div class="prop-row" :class="{ 'prop-row--start': alignStart }">
    <span class="prop-key">{{ label }}</span>
    <div class="prop-val" :class="{ 'prop-val--stack': valueStack }">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.prop-row {
  display: grid;
  grid-template-columns: var(--event-prop-label-w, 72px) minmax(0, 1fr);
  align-items: center;
  gap: var(--app-space-lg);
  padding: var(--app-space-md) var(--app-space-lg);
  min-height: var(--app-prop-row-height);
  box-sizing: border-box;
}

.prop-row--start {
  align-items: start;
}

.prop-key {
  color: var(--theme-text-muted);
  font-size: var(--app-font-size-md);
  line-height: var(--app-line-height-normal);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.prop-val {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--app-space-md);
  min-width: 0;
  overflow: hidden;
}

.prop-val--stack {
  flex-direction: column;
  align-items: stretch;
}

.prop-val :deep(.cell-input),
.prop-val :deep(.app-select-field),
.prop-val :deep(textarea.cell-input),
.prop-val :deep(.number-stepper-field),
.prop-val :deep(.resource-select-field),
.prop-val :deep(.story-select-field),
.prop-val :deep(.variable-index-field),
.prop-val :deep(.font-select-field),
.prop-val :deep(.image-option-picker-field) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.prop-val :deep(.cell-input.sm),
.prop-val :deep(.app-select-field__trigger.sm),
.prop-val :deep(input[type='number'].cell-input) {
  min-width: 100px;
}

.prop-val :deep(.number-stepper-field.cell-input.sm) {
  --number-stepper-sm-min-width: var(--number-stepper-min-width);
}

.prop-val :deep(.sub),
.prop-val :deep(.hint),
.prop-val :deep(.mini-label) {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>

<!-- 斑马纹依赖父级 .event-prop-form 直接子元素，与 scoped 无关 -->
<style>
.event-prop-form > .prop-row:nth-child(odd) {
  background: var(--theme-prop-row-a);
}

.event-prop-form > .prop-row:nth-child(even) {
  background: var(--theme-prop-row-b);
}
</style>
